import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { IDetour, IPOI } from "./map";
import Status from "./status";

export function Card({ item }: { item: IDetour | IPOI }) {
  const firstFase = "detourType" in item ? item.fases?.at(0) : null;
  const lastFase = "detourType" in item ? item.fases?.at(-1) : null;
  const customFase = {
    beginDate: firstFase?.beginDate,
    beginDateAlternative: firstFase?.beginDateAlternative,
    endDate: lastFase?.endDate,
    endDateAlternative: lastFase?.endDateAlternative,
  };

  return (
    <a
      href={"detourType" in item ? `werkzaamheden/${item.slug}` : `point-of-interests/${item.slug}`}
      className="card flex flex-col group border-b-[3px] hover:border-b-primary-accent w-full border-stroke border-2 rounded overflow-hidden not-prose focus:border-2 focus:border-blue-600"
    >
      <div className="relative overflow-hidden">
        <img
          className="w-full h-36 object-cover"
          src={item.coverPhoto.url}
          width={400}
          height={400}
          alt={item.name}
        />
        <Status item={item} />
      </div>
      <div className="flex flex-col gap-4 p-4 md:p-6 ">
        <h3 className="line-clamp-2">{item.name}</h3>
        <div className="rounded-sm flex max-md:flex-col gap-4 md:justify-between p-4 bg-secondary-background w-full">
          <div className="flex flex-col gap-y-2">
            <strong>Wanneer</strong>
            <PeriodString fase={customFase} />
          </div>

          <div className="flex flex-col gap-y-2">
            <strong>Reisadvies</strong>
            {"detourType" in item && (
              <div className="flex gap-2 [&>:nth-child(n+4)]:hidden min-h-6 col-span-2">
                <Transportations
                  title="Afgesloten voor"
                  items={item.fases[0].blockedTransportations}
                  className="bg-danger h-7 w-7 text-xl"
                />
                <Transportations
                  title="Beperkt bereikbaar voor"
                  items={item.fases[0].limitedTransportations}
                  className="bg-amsterdam-orange h-7 w-7 text-xl"
                />
                <Transportations
                  title="Te bereiken als"
                  items={item.fases[0].availableTransportations}
                  className="bg-succes h-7 w-7 text-xl"
                />
              </div>
            )}
          </div>
        </div>
        <div
          className="prose prose-h1:m-0 prose-p:m-0 prose-ul:m-0 prose-li:my-1 text-muted space-y-4"
          dangerouslySetInnerHTML={{
            __html: item.description.substring(0, 60).concat("..."),
          }}
        ></div>
        <div className="flex justify-between border-typography-black pb-2 border-b">
          <span className="font-medium">
            Meer informatie<span className="sr-only">over {item.name}</span>
          </span>
          <span className="flex items-center justify-center text-xl w-6 h-6 group-hover:bg-primary-accent rounded-full transition-all">
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </a>
  );
}

function Transportations({
  items,
  className,
  title,
}: {
  items: { id: string; name: string }[];
  className: string;
  title: string;
}) {
  return items.map((item) => (
    <span
      className={clsx(
        className,
        "p-[3px] flex justify-center items-center h-6 rounded-sm text-typography-white"
      )}
      title={`${title} "${item.name}"`}
    >
      <span className="sr-only">{`${title} "${item.name}"`}</span>
      <i className={clsx(getRIClass(item.name), "not-sr-only")}></i>
    </span>
  ));
}

export function getRIClass(name: string) {
  switch (name.toLowerCase()) {
    case "auto":
      return "ri-car-line";
    case "boot":
      return "ri-sailboat-line";
    case "fiets":
      return "ri-riding-line";
    case "metro":
      return "ri-subway-line";
    case "ov-bus":
      return "ri-bus-line";
    case "scheepvaart":
      return "ri-ship-line";
    case "tram":
      return "ri-train-line";
    case "trein":
      return "ri-train-fill";
    case "voetganger":
      return "ri-walk-line";
    case "vrachtverkeer":
      return "ri-truck-line";

    default:
      return "ri-barricade-line";
  }
}

interface IPeriodProps {
  beginDate?: string;
  endDate?: string;
  beginDateAlternative?: string;
  endDateAlternative?: string;
}
export function PeriodString({ fase }: { fase: IPeriodProps }): JSX.Element {
  const format = "dd MMM yyyy";
  const beginDate = fase.beginDateAlternative || formatDateAsString(fase.beginDate, format);
  const endDate = fase.endDateAlternative || formatDateAsString(fase.endDate, format);

  if (!endDate) {
    return <>{beginDate || "Nader te bepalen datum"}</>;
  }

  return <>{beginDate ? `Van ${beginDate} t/m ${endDate}` : "Nader te bepalen datum"}</>;
}

export function formatDateAsString(date?: string, $format?: string): string | null {
  if (date) {
    if (!$format) $format = "yyyy-MM-dd HH:mm";

    try {
      const parsed = parseISO(`${date}`);

      return format(parsed, $format, { locale: nl });
    } catch (error) {
      console.error("Error in date", error);
    }
  }
  return null;
}
